import { cn } from '@resolute/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';


import { type NavMenu } from './nav-menu';
import { highlightTransition } from './nav-styles';

import { EASE_OUT_EXPO, PANEL_TRANSITION } from '@/lib/motion';
import { useHoverHighlight } from '@/lib/use-hover-highlight';


/**
 * A desktop top-level item with a hover/focus mega-menu (disclosure pattern),
 * rendered as a premium two-panel layout: a dark category sidebar on the left
 * whose hovered/focused entry drives the white content panel on the right. The
 * links themselves are unchanged — same `to`, same search/URL behaviour.
 */
export function NavDropdown({
  menu,
  isOpen,
  onOpen,
  onHoverOpen,
  onToggle,
  onCloseRequest,
  onScheduleClose,
  onMeasure,
}: {
  menu: NavMenu;
  isOpen: boolean;
  /** Instant open — keyboard (Tab) focus only. */
  onOpen: () => void;
  /** Debounced open — mouse hover, so a fleeting scroll-induced overlap under
   *  a stationary cursor gets cancelled before it ever opens. */
  onHoverOpen: () => void;
  onToggle: () => void;
  onCloseRequest: () => void;
  /** Debounced close — used when the pointer leaves this item for a sibling
   *  (another dropdown, a plain nav link) rather than the parent nav row, so
   *  hovering away from an open menu onto e.g. "About Us" actually closes it. */
  onScheduleClose: () => void;
  /** Reports this trigger's element to the shared top-level nav highlight. */
  onMeasure: (el: HTMLElement | null) => void;
}) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const liRef = useRef<HTMLLIElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Same persistent-highlight technique as the top-level nav — see
  // useHoverHighlight. sidebarButtonRefs lets the "active category" effect
  // below measure the current category even when it changes programmatically
  // (e.g. resetting to the first category on reopen), not just from an event.
  const sidebarHighlight = useHoverHighlight<HTMLUListElement>();
  const itemHighlight = useHoverHighlight<HTMLUListElement>();
  // Pulled out so the effects below can depend on the callbacks themselves.
  // `useHoverHighlight` returns a fresh object each render, so depending on
  // `sidebarHighlight`/`itemHighlight` would re-run the effects every render —
  // `measure` and `clear` are stable `useCallback`s, and are what the effects
  // actually use.
  const { measure: measureSidebar } = sidebarHighlight;
  const { clear: clearItemHighlight } = itemHighlight;
  const sidebarButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Clicking the trigger natively focuses it too, which would otherwise fire
  // onFocus's onOpen() right alongside onClick's onToggle() — forcing the menu
  // back open even when the click just closed it (and letting an unrelated
  // later focus event, e.g. a modal returning focus, spuriously reopen it).
  // Set on pointerdown (fires before focus) so onFocus can tell a click-caused
  // focus apart from genuine keyboard (Tab) focus and only open for the latter.
  const pointerFocusRef = useRef(false);
  // Horizontal offset applied to keep the fixed-width panel inside the viewport.
  const [shift, setShift] = useState(0);

  // Always reopen on the first category.
  useEffect(() => {
    if (!isOpen) setActive(0);
  }, [isOpen]);

  // Keep the sidebar's traveling highlight synced to the active category —
  // covers hover/focus (which also call sidebarHighlight.measure directly)
  // AND programmatic changes (opening, or the reset-to-first-category above).
  useLayoutEffect(() => {
    if (!isOpen) return;
    measureSidebar(sidebarButtonRefs.current[active] ?? null);
  }, [active, isOpen, measureSidebar]);

  // The item grid remounts (key={active}) on category switch — clear any
  // stale item highlight from the previous category.
  useEffect(() => {
    clearItemHighlight();
  }, [active, clearItemHighlight]);

  // Collision-aware positioning: the panel opens `left-0` from its trigger, so a
  // right-side trigger (e.g. Services) can spill past the viewport edge at
  // smaller desktop widths. Measure once open (and on resize) and pull it back
  // in via `left` — a plain CSS property that framer-motion's transform-based
  // entrance animation won't clobber. Anchored off the trigger's own rect, which
  // (unlike the panel) isn't scaled by the entrance animation.
  useLayoutEffect(() => {
    if (!isOpen) {
      setShift(0);
      return;
    }
    const GUTTER = 8;
    const measure = () => {
      const li = liRef.current;
      const panel = panelRef.current;
      if (!li || !panel) return;
      const left = li.getBoundingClientRect().left;
      const width = panel.offsetWidth; // layout width — unaffected by transforms
      let s = 0;
      const overflowRight = left + width - (window.innerWidth - GUTTER);
      if (overflowRight > 0) s = -overflowRight;
      // Never push the panel off the left edge.
      if (left + s < GUTTER) s = GUTTER - left;
      setShift(s);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  const group = menu.groups[active] ?? menu.groups[0];

  // Every group is built from `/filter-options`, so there are none until that
  // resolves — and none at all if it fails. Fall back to a plain link to the
  // CTA target rather than opening a mega-menu with an empty panel (which
  // would also have nothing to read `group` from).
  if (!group) {
    const to = menu.cta?.to ?? '/';
    return (
      // Deliberately the same box as the real trigger below — same padding,
      // same text size, same chevron — so the nav row does not reflow when
      // `/filter-options` resolves and this becomes a full mega-menu. Rendering
      // a plain link here instead made every nav item shift sideways mid-load.
      // It navigates rather than opening a panel, since there is nothing to
      // show yet.
      <li className="relative">
        <NavLink
          to={to}
          className={({ isActive }) =>
            cn(
              'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-fast ease-out-expo hover:bg-muted',
              isActive ? 'text-primary' : 'text-foreground',
            )
          }
          onMouseEnter={(e) => onMeasure(e.currentTarget)}
          onFocus={(e) => onMeasure(e.currentTarget)}
        >
          {menu.label}
          <ChevronDown className="size-4" aria-hidden="true" />
        </NavLink>
      </li>
    );
  }

  return (
    <li
      ref={liRef}
      className="relative"
      onMouseEnter={() => {
        onHoverOpen();
        onMeasure(triggerRef.current);
      }}
      onMouseLeave={onScheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={`menu-${menu.id}`}
        onClick={onToggle}
        onPointerDown={() => {
          pointerFocusRef.current = true;
        }}
        onFocus={(e) => {
          onMeasure(e.currentTarget);
          if (pointerFocusRef.current) {
            pointerFocusRef.current = false;
            return;
          }
          onOpen();
        }}
        className={cn(
          'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-fast ease-out-expo hover:bg-muted',
          isOpen ? 'text-primary' : 'text-foreground',
        )}
      >
        {menu.label}
        <ChevronDown
          className={cn('size-4 transition-transform duration-fast', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={panelRef}
            id={`menu-${menu.id}`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            transition={PANEL_TRANSITION}
            style={{ left: shift }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) onCloseRequest();
            }}
            // The height has to clear the SIDEBAR, not the item grid: the grid
            // scrolls when it overflows, but the sidebar doesn't — its category
            // list plus the `mt-auto` CTA sit in this fixed box under
            // `overflow-hidden`, so too many categories silently clip the CTA.
            // 480px fits 8 categories (~40px each) + eyebrow + CTA with ~40px
            // to spare. Raise it again if a ninth group is ever added.
            className="absolute left-0 top-full mt-2 flex h-[480px] w-[45rem] max-w-[calc(100vw-1rem)] origin-top-left overflow-hidden rounded shadow-md"
          >
            {/* Left — deep-navy category sidebar in the logo's exact brand
                palette: the wordmark navy (hue 249) with the fresh green mark
                (hue 100) as the accent. Uses the `sidebar-*` tokens (tokens.css)
                — darker/lighter steps of the same brand hues, formalised so
                this dark-on-brand treatment is reusable, not one-off values. */}
            <div className="flex w-52 shrink-0 flex-col bg-sidebar p-3">
              <p className="px-3 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground-muted">
                {menu.eyebrow ?? menu.label}
              </p>
              <ul ref={sidebarHighlight.containerRef} className="relative flex flex-col gap-0.5">
                {/* Persistent, gliding highlight — see useHoverHighlight; a
                    single element that moves avoids the mount/unmount flicker
                    a per-item shared layoutId can suffer. */}
                <motion.span
                  aria-hidden="true"
                  style={{ pointerEvents: 'none' }}
                  className="absolute rounded-md bg-sidebar-elevated"
                  animate={
                    sidebarHighlight.rect ? { ...sidebarHighlight.rect, opacity: 1 } : { opacity: 0 }
                  }
                  transition={highlightTransition(reduce)}
                />
                {menu.groups.map((g, i) => {
                  const Icon = g.icon;
                  const isActive = i === active;
                  return (
                    <li key={g.heading}>
                      <button
                        ref={(el) => {
                          sidebarButtonRefs.current[i] = el;
                        }}
                        type="button"
                        aria-current={isActive || undefined}
                        onMouseEnter={(e) => {
                          setActive(i);
                          sidebarHighlight.measure(e.currentTarget);
                        }}
                        onFocus={(e) => {
                          setActive(i);
                          sidebarHighlight.measure(e.currentTarget);
                        }}
                        className={cn(
                          'group relative flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-base ease-out-expo',
                          isActive ? 'text-white' : 'text-sidebar-foreground hover:text-white',
                        )}
                      >
                        {Icon && (
                          <Icon
                            className={cn(
                              'size-4 shrink-0 transition-colors duration-base ease-out-expo',
                              isActive
                                ? 'text-sidebar-accent'
                                : 'text-sidebar-icon group-hover:text-sidebar-accent-hover',
                            )}
                            aria-hidden="true"
                          />
                        )}
                        <span className="truncate">{g.heading}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {menu.cta && (
                <div className="mt-auto border-t border-sidebar-border pt-3">
                  <Link
                    to={menu.cta.to}
                    className="group/cta flex items-center justify-center gap-1.5 rounded-md bg-secondary px-3 py-2.5 text-sm font-semibold text-secondary-foreground transition-colors duration-fast ease-out-expo hover:bg-secondary/90"
                  >
                    {menu.cta.label}
                    <ArrowUpRight
                      className="size-4 transition-transform duration-fast ease-out-expo group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
                      aria-hidden="true"
                    />
                  </Link>
                </div>
              )}
            </div>

            {/* Right — content panel. `pb-5` (not `p-7`'s 28px on every other
                side) so the panel's own bottom padding matches the divider's
                `mb-5` gap above the grid — equalizing top/bottom around the
                items without inflating either past the panel's normal rhythm
                (matching them by raising the divider's margin instead, as an
                earlier pass did, made the top gap noticeably bigger than the
                gap between rows). */}
            <div className="flex h-full min-w-0 flex-1 flex-col bg-popover p-7 pb-5 text-popover-foreground">
              <div className="flex items-start gap-4">
                {group.icon && (
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-border bg-muted/60 text-primary">
                    <group.icon className="size-5" aria-hidden="true" />
                  </span>
                )}
                <div className="min-w-0">
                  <h3 className="font-display text-xl font-semibold leading-tight tracking-tight text-foreground">
                    {group.heading}
                  </h3>
                  {group.description && (
                    <p className="mt-1 max-w-md text-sm leading-snug text-muted-foreground">
                      {group.description}
                    </p>
                  )}
                </div>
              </div>

              <hr className="my-5 border-border" />

              <motion.ul
                key={active}
                ref={itemHighlight.containerRef}
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
                className={cn(
                  // `overflow-y-auto` only engages when the rows genuinely
                  // exceed the panel — the taxonomy is API-driven now, so a
                  // group can hold 14 entries (7 rows) where the old hardcoded
                  // lists never passed 4. Scrolling keeps every option
                  // reachable instead of clipping the tail against the panel's
                  // fixed height; short groups are unaffected.
                  //
                  // `-mx-3 px-3` cancels out: it widens the scroll box by the
                  // same 12px the item links bleed sideways with their own
                  // `-mx-3` (and the hover highlight tracks), so that bleed
                  // lands in this element's padding instead of counting as
                  // horizontal overflow. Without it, `overflow-y-auto` forces
                  // overflow-x from `visible` to `auto` per spec and the panel
                  // grows a spurious horizontal scrollbar. Item alignment is
                  // unchanged — the content box lands exactly where it did.
                  'relative -mx-3 grid min-h-0 flex-1 grid-cols-2 gap-x-10 overflow-y-auto overflow-x-hidden px-3 [scrollbar-width:thin]',
                  // 2 or fewer items is a single row (grid-cols-2) — top-align
                  // it rather than centering it in the fixed-height panel.
                  // 3-6 (up to 3 rows) spreads evenly to fill the panel
                  // instead of sitting top-aligned with dead space below.
                  // Beyond that the list scrolls, so it must start at the top —
                  // `content-evenly` would push the first row out of view.
                  group.items.length > 6
                    ? 'content-start'
                    : group.items.length > 2
                      ? 'content-evenly'
                      : 'content-start',
                )}
              >
                {/* Persistent, gliding highlight — see useHoverHighlight; a
                    single element that moves avoids the mount/unmount flicker
                    a per-item shared layoutId can suffer. */}
                <motion.span
                  aria-hidden="true"
                  style={{ pointerEvents: 'none' }}
                  className="absolute rounded-lg bg-muted"
                  animate={itemHighlight.rect ? { ...itemHighlight.rect, opacity: 1 } : { opacity: 0 }}
                  transition={highlightTransition(reduce)}
                />
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onMouseEnter={(e) => itemHighlight.measure(e.currentTarget)}
                      onMouseLeave={itemHighlight.clear}
                      onFocus={(e) => itemHighlight.measure(e.currentTarget)}
                      onBlur={itemHighlight.clear}
                      className="group relative -mx-3 block rounded-lg border-b border-border/70 px-3 py-4 transition-colors duration-fast ease-out-expo hover:border-primary/40"
                    >
                      <span className="relative flex items-center justify-between gap-2">
                        <span className="text-[0.95rem] font-semibold text-foreground transition-colors duration-fast ease-out-expo group-hover:text-primary">
                          {item.label}
                        </span>
                        <ArrowUpRight
                          className="size-4 shrink-0 -translate-x-1 text-secondary opacity-0 transition-[opacity,transform] duration-fast ease-out-expo group-hover:translate-x-0 group-hover:opacity-100"
                          aria-hidden="true"
                        />
                      </span>
                      {item.sublabel && (
                        <span className="relative mt-1 block text-sm leading-snug text-muted-foreground transition-colors duration-fast ease-out-expo group-hover:text-foreground/70">
                          {item.sublabel}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </motion.ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}
